import { createFileRoute, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';

import { queryClient } from '~/api/queryClient';
import { queryKeys } from '~/api/queryKeys';
import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';
import { fetchScheduledJobStatus } from '~/features/content-enrichment/services/scheduled-enrichment.service';

const EnrichmentOverviewPage = lazy(
  () => import('~/features/content-enrichment/pages/enrichment-overview-page'),
);

/**
 * Route gate: blocks Enrichment Overview only when the pipeline was never
 * triggered at all (a direct/deep-linked visit before Full Catalog Generation
 * ever ran). Deliberately does NOT check `pipeline.status` — that snapshot
 * lags behind the live per-workflow trigger-status endpoint after a retry
 * (the aggregate job-status refresh races backend eventual consistency), so a
 * status-based check would keep re-redirecting a genuinely-finished retry
 * back to Full Catalog Generation. Existence isn't subject to that race:
 * once a pipeline has been triggered, this block never reverts to absent.
 * Fails open on fetch errors so the page's own states still render.
 */
export const Route = createFileRoute(
  '/_authenticated/_tenant/enrichments/meta/content-enrichment/$batchId/overview',
)({
  beforeLoad: async ({ params }) => {
    let status;
    try {
      status = await queryClient.ensureQueryData({
        queryKey: queryKeys.scheduledEnrichment.jobStatus(params.batchId),
        queryFn: () => fetchScheduledJobStatus(params.batchId),
      });
    } catch {
      return;
    }

    if (!status.pipeline) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router uses throw redirect() as control flow
      throw redirect({
        to: '/enrichments/meta/content-enrichment/$batchId/full',
        params: { batchId: params.batchId },
      });
    }
  },
  component: () => (
    <Suspense fallback={<PageSkeleton />}>
      <EnrichmentOverviewPage />
    </Suspense>
  ),
});
